import { useEffect, useState } from "react";
import { App, Form, Input, Modal, Select, Space, Upload } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { PaperClipOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelect } from "@refinedev/antd";

import { apiClient } from "../../../services/api/apiClient";
import { RichTextEditor } from "../../../components/common/RichTextEditor";

interface CreateIncidenceModalProps {
  open: boolean;
  auditId: number;
  onClose: () => void;
  /** Fired after a successful create. The argument is the new
   *  incidence id so the parent can navigate to it if it wants. */
  onCreated?: (incidenceId: number) => void;
}

interface FormValues {
  kind_incidence: number;
  subject: string;
  description: string;
}

/**
 * Compact modal for creating an incidence from the audit detail page.
 *
 * Fields:
 *   - Tipo de incidencia (kind_incidence)
 *   - Asunto (subject)
 *   - Mensaje (description, rich text via Quill)
 *   - Adjuntar archivo (single optional attachment)
 *
 * Date and user are filled server-side: `created` by TimeStampedModel
 * and `reported_by` by the IncidenceSerializer.create() override.
 */
export const CreateIncidenceModal = ({
  open,
  auditId,
  onClose,
  onCreated,
}: CreateIncidenceModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  // Body is kept in component state because the RichTextEditor needs
  // to be driven imperatively — antd Form.Item can't directly own a
  // Quill instance.
  const [body, setBody] = useState<string>("");

  // Reset everything when the modal opens or closes.
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setFileList([]);
    setBody("");
  }, [open, form]);

  // After the Incidence → Ticket unification, audit incidence types
  // live as `TicketCategory` rows with `kind=audit`. We hit the
  // ticket-categories endpoint and filter client-side because the
  // legacy list endpoint isn't paginated.
  const { selectProps: kindIncidenceSelectProps } = useSelect({
    resource: "v1/ticket-categories",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 200 },
    filters: [
      { field: "kind", operator: "eq", value: "audit" },
    ],
  });

  const stripHtml = (html: string) => html.replace(/<(.|\n)*?>/g, "").trim();

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!stripHtml(body)) {
      message.error(
        t("incidence.body_required", "El mensaje no puede estar vacío"),
      );
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("audit", String(auditId));
      // `category` is the unified TicketCategory id (formerly
      // `kind_incidence`). The field name is reused for clarity in
      // the form schema — `values.kind_incidence` carries the id.
      fd.append("category", String(values.kind_incidence));
      fd.append("subject", values.subject);
      fd.append("description", body);
      fd.append("priority", "medium");
      fd.append("requires_immediate_action", "false");

      const file = fileList[0]?.originFileObj;
      if (file) fd.append("attachment", file as File);

      const res = await apiClient.post<{ id: number }>("/v1/tickets/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      message.success(
        t("incidence.create_success", "Incidencia creada exitosamente"),
      );
      onCreated?.(res.data?.id);
      onClose();
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ??
        err?.response?.data?.description?.[0] ??
        err?.message;
      message.error(
        detail ?? t("incidence.create_error", "Error al crear la incidencia"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t("incidence.create", "Crear Incidencia")}
      onCancel={() => (submitting ? undefined : onClose())}
      onOk={handleSubmit}
      okText={t("common.create", "Crear")}
      cancelText={t("common.cancel", "Cancelar")}
      okButtonProps={{ loading: submitting }}
      cancelButtonProps={{ disabled: submitting }}
      width={720}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          label={t("incidence.type", "Tipo de incidencia")}
          name="kind_incidence"
          rules={[
            {
              required: true,
              message: t(
                "incidence.type_required",
                "Selecciona un tipo de incidencia",
              ),
            },
          ]}
        >
          <Select
            {...kindIncidenceSelectProps}
            placeholder={t("incidence.type_placeholder", "Selecciona un tipo")}
            showSearch
            // Render the dropdown inside the modal so it inherits the
            // Modal's stacking context and never gets covered by it.
            // The explicit z-index is a belt-and-braces guard for
            // setups where the modal sits in a sibling portal.
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            dropdownStyle={{ zIndex: 10000 }}
            filterOption={(input, option) =>
              String(option?.label ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item
          label={t("incidence.subject", "Asunto")}
          name="subject"
          rules={[
            {
              required: true,
              message: t(
                "incidence.subject_required",
                "Introduce un asunto",
              ),
            },
            { max: 200 },
          ]}
        >
          <Input
            placeholder={t(
              "incidence.subject_placeholder",
              "Resumen breve de la incidencia",
            )}
            maxLength={200}
            showCount
          />
        </Form.Item>

        <Form.Item
          label={t("incidence.message", "Mensaje")}
          required
          // The RichTextEditor lives in component state, so Form.Item
          // just renders the wrapper — no `name` here.
        >
          <RichTextEditor
            value={body}
            onChange={setBody}
            placeholder={t(
              "incidence.message_placeholder",
              "Describe la incidencia con detalle…",
            )}
            minHeight={200}
          />
        </Form.Item>

        <Form.Item label={t("incidence.attachment", "Adjuntar archivo")}>
          <Upload
            beforeUpload={() => false}
            fileList={fileList}
            onChange={({ fileList: list }) => setFileList(list.slice(-1))}
            onRemove={() => setFileList([])}
            maxCount={1}
          >
            <Space style={{ cursor: "pointer", color: "#1677ff" }}>
              <PaperClipOutlined />
              {t("incidence.pick_file", "Seleccionar archivo")}
            </Space>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateIncidenceModal;
